import {AfterViewInit, Component, computed, ElementRef, inject, Injector, Renderer2, signal} from "@angular/core";
import {Translations} from "@translations";
import {BasicMaterialDialog, MatDialogDataBaseInput} from "../../basic-material-dialog/basic-material-dialog.directive";
import {Router} from "@angular/router";
import {SimStatusPalette} from "../../../../../shared/models/SimStatusPalette";
import confetti from "canvas-confetti";
import {forkJoin} from "rxjs";
import {SessionType} from "@models";

export interface DialogData_SessionCompleteDialogInput extends MatDialogDataBaseInput {
	onCloseNavigationLink: string;
	sessionTitle: string;
	sessionType: SessionType;
}

const colorArray = ["#279989", "#FFFFFF", "#6eceb2", "#4db6ac", "#26a69a", "#009688", "#00897b", "#00796b", "#00695c", "#004d40"];

@Component({
	selector: "sim-session-complete-dialog",
	templateUrl: "./session-complete-dialog.component.html",
	styleUrl: "./session-complete-dialog.component.scss",
	standalone: false,
})
export class SessionCompleteDialogComponent
	extends BasicMaterialDialog<SessionCompleteDialogComponent, DialogData_SessionCompleteDialogInput>
	implements AfterViewInit
{
	// Constants
	public readonly T = Translations;
	protected readonly SimStatusPalette = SimStatusPalette;

	// Properties
	protected readonly title = signal<string>(this.data.sessionTitle);
	protected readonly type = signal<SessionType>(this.data.sessionType);
	protected readonly content = computed<{paragraph: string; fontIcon: string}>(() => this.computeContent(this.type()));
	readonly #canvas: HTMLCanvasElement;

	// Injections
	readonly #router = inject(Router);
	readonly #renderer = inject(Renderer2);

	constructor(
		injector: Injector,
		private elementRef: ElementRef,
	) {
		super(injector);
		this.#canvas = this.#renderer.createElement("canvas");
		this.#canvas.className = "confettiCanvas";
		this.#renderer.appendChild(this.elementRef.nativeElement, this.#canvas);
	}

	public ngAfterViewInit(): void {
		const confettiRender = confetti.create(this.#canvas, {
			resize: true, // will fit all screen sizes
		});
		forkJoin([
			confettiRender({
				colors: colorArray,
				particleCount: 250,
				angle: 45,
				spread: 75,
				drift: 0.75,
				origin: {y: 1, x: 0},
				startVelocity: 95,
			}),
			confettiRender({
				colors: colorArray,
				particleCount: 250,
				angle: 135,
				spread: 75,
				drift: -0.75,
				origin: {y: 1, x: 1},
				startVelocity: 95,
			}),
		]);
	}

	protected async closeDialog() {
		await this.#router.navigate([this.data.onCloseNavigationLink]);
	}

	private computeContent(type: SessionType): {paragraph: string; fontIcon: string} {
		const result = {paragraph: "", fontIcon: ""};
		switch (type) {
			case SessionType.Exam:
				result.paragraph = this.T.EXAM_COMPLETE_DIALOG_PARAGRAPH;
				result.fontIcon = "fa-cat-space";
				break;
			case SessionType.PeerPractice:
				result.paragraph = this.T.PEER_PRACTICE_COMPLETE_DIALOG_PARAGRAPH;
				result.fontIcon = "fa-narwhal";
				break;
			case SessionType.MultiRoomRecording:
				result.paragraph = this.T.SESSION_GENERIC_COMPLETE_DIALOG_PARAGRAPH;
				result.fontIcon = "fa-squirrel";
		}
		return result;
	}
}
